// React Component for Chart Sizes

const { useMemo, useState } = wp.element;
import useDatabaseCleanerContext from "../context";
import { NekoTheme } from '@neko-ui';

const ChartStyle = {
  maxWidth: '100%', display: 'flex', justifyContent: 'flex-end', 
  alignItems: 'flex-end', height: 38, marginRight: 10
}

const GrayPillStyle = {
  borderRadius: '1px 1px 0px 0px',
  background: NekoTheme.gray,
  transition: 'height 0.2s ease-out'
}

const RedPillStyle = {
  borderRadius: '1px 1px 0px 0px',
  background: NekoTheme.red,
  transition: 'height 0.2s ease-out'
}

const GreenPillStyle = {
  borderRadius: '0px 0px 1px 1px',
  background: NekoTheme.green,
  transition: 'height 0.2s ease-out'
}

const barLabel = {
  fontSize: 6,
  lineHeight: '10px',
  height: 5,
  textAlign: 'center',
}

const BarStyle = {
  paddingLeft: 1,
  paddingRight: 1,
  width: 6.5,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  cursor: 'pointer',
}

const BarInfo = {
  fontSize: 11,
  textAlign: 'right',
  marginRight: 10,
  height: '100%',
  lineHeight: '12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}

const heightInPixels = 32;

const ChartSizes = () => {
  const [ currentBar, setCurrentBar ] = useState();
  const { totalSizes: contextSizes } = useDatabaseCleanerContext();
  const randomEnabled = contextSizes.length < 3;
  const [ mode, setMode ] = useState('size');

  const totalSizes = useMemo(() => {
    if (randomEnabled) {
      const fakeData = [];
      const date = new Date();
      date.setDate(date.getDate() - 7);
      for (let i = 0; i < 8; i++) {
        const dateString = date.toISOString().split('T')[0];
        const randomSize = Math.floor(Math.random() * 10) + 530;
        fakeData.push({ date: dateString, size: randomSize });
        date.setDate(date.getDate() + 1);
      }
      return fakeData;
    }
    return contextSizes;
  }, [contextSizes]);

  const chartLog = (value) => {
    if (value > 0) {
      return Math.log(1.2 + value);
    }
    else if (value < 0) {
      return -Math.log(1.2 + (value * -1));
    }
    else {
      return 0;
    }
  }

  
  // Bars with +/- changes
  const changeBars = useMemo(() => {
    let sizes = totalSizes.slice(-60);
    let previousSize = null;
    sizes = sizes.map(size => {
      const change = previousSize ? (size.size - previousSize) / previousSize * 100 : 0;
      const changeLog = chartLog(change);
      const date = new Date(size.date);
      const day = date.getDate();
      let bar = { date: size.date.replaceAll('-', '/'), day, size: size.size,
        change: Math.round(change * 100) / 100,
        changeLog,
      };
      previousSize = size.size;
      return bar;
    });
    const minChange = Math.min(...sizes.map((o) => o.changeLog));
    const maxChange = Math.max(...sizes.map((o) => o.changeLog));
    const totalChange = maxChange - minChange;
    const heightMinChange = (Math.abs(minChange) * 100) / totalChange;
    sizes = sizes.map(size => {
      let height = totalChange ? (Math.abs(size.changeLog) * 100) / totalChange : 0;
      height = Math.round(height * 100) / 100;
      return {
        ...size, height,
        isUp: size.changeLog > 0,
        heightSpace: totalChange ? (size.changeLog > 0 ? heightMinChange : heightMinChange - height) : heightInPixels,
      }
    });
    //console.log('changeBars', sizes);
    return sizes;
  }, [totalSizes]);

  const bars = useMemo(() => {
    let sizes = totalSizes.slice(-60);
    let previousSize = null;
    sizes = sizes.map(size => {
      const change = previousSize ? (size.size - previousSize) / previousSize * 100 : 0;
      const changeLog = chartLog(change);
      const value = size.size;
      const valueLog = chartLog(value);
      const date = new Date(size.date);
      const day = date.getDate();
      let bar = { date: size.date.replaceAll('-', '/'), day, 
        size: size.size, value, valueLog,
        change: Math.round(change * 100) / 100,
        changeLog,
      };
      previousSize = size.size;
      return bar;
    });
    const min = Math.min(...sizes.map((o) => o.value));
    const max = Math.max(...sizes.map((o) => o.value));
    const totalChange = max - min;
    sizes = sizes.map(size => {
      return {
        ...size,
        isUp: size.change > 0,
        height: ((size.value - min) / totalChange) * 80 + 20, // The % height of the actual bar
        heightSpace: 0, // The % height at the bottom
        heightInPixels
      }
    });
    return sizes;
  }, [totalSizes]);

  const finalBars = mode === 'size' ? bars : changeBars;

  return (
    <div style={ChartStyle} onClick={() => { setMode(mode === 'size' ? 'change' : 'size') }}>
      {currentBar && <div style={BarInfo}>
        <div>
          <b>{randomEnabled ? "This is fake data! 😎" : currentBar?.date}</b><br />
          {randomEnabled && <span>Come back in 3-4 days.</span>}
          {!randomEnabled &&<span>
           {currentBar?.size} MB ({currentBar?.change}%)
          </span>}
        </div>
      </div>
      }
      {finalBars.map((bar) => (
        <div key={bar.date} onMouseEnter={() => { setCurrentBar(bar) }} onMouseLeave={() => { setCurrentBar() }}
            style={{ ...BarStyle, background: bar?.date === currentBar?.date ? 'lightgray' : 'inherit' }}>
          <div className="space-top"
            style={{
              flex: 'auto',
            }}
          />
          <div className="space-value"
            style={{...(randomEnabled ? GrayPillStyle : bar.isUp ? RedPillStyle : GreenPillStyle), 
              height: !bar.height ? 1 : `${bar.height / 100 * heightInPixels}px`,
            }}
          />
          <div className="space-bottom"
            style={{
              width: 6,
              height: `${bar.heightSpace / 100 * heightInPixels}px`
            }}
          />
          <div style={barLabel}>
            {bar.day}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChartSizes;
